// components/courier-dashboard/FilterBar.tsx
import { useEffect, useMemo, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import * as Popover from "@radix-ui/react-popover";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useApiServices } from "../../hooks/useApiServices";
import { API_ENDPOINTS } from "../../lib/types";
import type { FilterBarProps } from "./types";
import { DayPicker, DateRange } from "react-day-picker";
import { formatISO, format } from "date-fns";
import { es } from "date-fns/locale";
import "react-day-picker/dist/style.css";

export function FilterBar({ onFilterChange, filters }: FilterBarProps) {
  const { fetchFilters } = useApiServices();

  const { data: filterOptions, isLoading } = useQuery({
    queryKey: [API_ENDPOINTS.courier_filters],
    queryFn: fetchFilters,
  });

  // --- Rango por defecto: últimos 30 días (para botón limpiar) ---
  const defaultRange = useMemo(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - 30);
    return { from, to };
  }, []);

  // --- Popover controlado + rango "borrador" para el calendario ---
  const [open, setOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(undefined);

  // Sincroniza el calendario con el filtro actual cuando cambie externamente
  useEffect(() => {
    if (
      typeof filters.timeRange === "object" &&
      filters.timeRange?.from &&
      filters.timeRange?.to
    ) {
      setDraftRange({
        from: new Date(filters.timeRange.from),
        to: new Date(filters.timeRange.to),
      });
    } else {
      setDraftRange(undefined);
    }
  }, [filters.timeRange]);

  // Rango actual para etiqueta
  const currentRange: DateRange | undefined =
    typeof filters.timeRange === "object" &&
    filters.timeRange?.from &&
    filters.timeRange?.to
      ? { from: new Date(filters.timeRange.from), to: new Date(filters.timeRange.to) }
      : undefined;

  const labelRange =
    currentRange?.from && currentRange?.to
      ? `${format(currentRange.from, "dd MMM yyyy", { locale: es })} — ${format(currentRange.to, "dd MMM yyyy", { locale: es })}`
      : "Seleccionar rango";

  // No limpiamos el draft al abrir para mostrar el rango actual;
  // solo controlamos la apertura/cierre manualmente
  const handleOpenChange = (v: boolean) => setOpen(v);

  // Reinicia el rango si ya había uno completo (primer click = nuevo FROM)
  const handleDayClick = (day: Date) => {
    if (draftRange?.from && draftRange?.to) {
      setDraftRange({ from: day, to: undefined });
    }
  };

  // Aplica al segundo click (from+to definidos); cierra popover
  const handleRangeChange = (range?: DateRange) => {
    setDraftRange(range);
    if (range?.from && range?.to) {
      onFilterChange({
        ...filters,
        timeRange: { from: formatISO(range.from), to: formatISO(range.to) },
      });
      setOpen(false);
    }
  };

  // Reset general
  const handleClearAll = () => {
    onFilterChange({
      logisticOperators: [],
      couriers: [],
      shippingType: [],
      deliveryType: [],
      regions: [],
      communes: [],
      timeRange: { from: formatISO(defaultRange.from), to: formatISO(defaultRange.to) },
    });
  };

  // Helper: Select con botón de limpiar
  const renderClearableSelect = (
    label: string,
    values: string[],
    allOptions: string[] | undefined,
    onChange: (val: string | undefined) => void,
    widthClass = "w-[200px]"
  ) => {
    const selectedValue = values.length > 0 ? values[0] : undefined;

    return (
      <div className="flex items-center gap-2">
        <Select key={`${label}-${selectedValue ?? "EMPTY"}`} value={selectedValue} onValueChange={(val) => onChange(val)}>
          <SelectTrigger className={`${widthClass} bg-white`}>
            <SelectValue placeholder={label} />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {allOptions?.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedValue && (
          <button
            onClick={() => onChange(undefined)}
            className="text-gray-500 hover:text-gray-700 text-sm px-1"
            title={`Borrar ${label}`}
          >
            ✕
          </button>
        )}
      </div>
    );
  };

  if (isLoading) return <div>Cargando filtros...</div>;
  if (!filterOptions) return <div>Error al cargar los filtros</div>;

  return (
    <div className="flex flex-wrap items-center gap-4 mb-6">
      {/* 1) Operador Logístico */}
      {renderClearableSelect(
        "Operador Logístico",
        filters.logisticOperators,
        filterOptions.logisticOperators,
        (val) => onFilterChange({ ...filters, logisticOperators: val ? [val] : [] })
      )}

      {/* 2) Couriers */}
      {renderClearableSelect(
        "Couriers",
        filters.couriers,
        filterOptions.couriers,
        (val) => onFilterChange({ ...filters, couriers: val ? [val] : [] })
      )}

      {/* 3) Tipo de Entrega */}
      {renderClearableSelect(
        "Tipo de Entrega",
        filters.shippingType,
        filterOptions.shippingType,
        (val) => onFilterChange({ ...filters, shippingType: val ? [val] : [] }),
        "w-[180px]"
      )}

      {/* 4) Tipo de Despacho */}
      {renderClearableSelect(
        "Tipo de Despacho",
        filters.deliveryType,
        filterOptions.deliveryType,
        (val) => onFilterChange({ ...filters, deliveryType: val ? [val] : [] }),
        "w-[180px]"
      )}

      {/* 5) Región */}
      {renderClearableSelect(
        "Seleccionar Región",
        filters.regions,
        filterOptions.regions,
        (val) => onFilterChange({ ...filters, regions: val ? [val] : [] }),
        "w-[180px]"
      )}

      {/* 6) Comuna */}
      {renderClearableSelect(
        "Seleccionar Comuna",
        filters.communes,
        filterOptions.communes,
        (val) => onFilterChange({ ...filters, communes: val ? [val] : [] }),
        "w-[180px]"
      )}

      {/* 7) Rango de Fechas */}
      <Popover.Root open={open} onOpenChange={handleOpenChange}>
        <Popover.Trigger asChild>
          <button
            className="flex items-center gap-2 rounded-md border px-3 py-2 bg-white hover:bg-gray-50"
            title="Seleccionar rango de fechas"
          >
            <CalendarIcon className="h-4 w-4" />
            <span className="text-sm">{labelRange}</span>
            {currentRange?.from && currentRange?.to && (
              <X
                className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600"
                onClick={(e) => {
                  e.stopPropagation();
                  onFilterChange({
                    ...filters,
                    timeRange: { from: formatISO(defaultRange.from), to: formatISO(defaultRange.to) },
                  });
                }}
              />
            )}
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className="rounded-md border bg-white p-3 shadow-lg z-50"
            sideOffset={8}
            // Evita cierres por focus/pointer fuera hasta terminar selección
            onPointerDownOutside={(e) => e.preventDefault()}
            onFocusOutside={(e) => e.preventDefault()}
          >
            <DayPicker
              mode="range"
              numberOfMonths={2}
              selected={draftRange}
              onSelect={handleRangeChange}
              onDayClick={handleDayClick}
              locale={es}
              captionLayout="dropdown"
              disabled={{ after: new Date() }}
            />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* Limpiar */}
      <button onClick={handleClearAll} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
        Limpiar Filtros
      </button>
    </div>
  );
}